import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Link,
  useTheme,
  useMediaQuery,
  Stack,
  TextField,
  Divider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import {
  Public as GeographicIcon,
  Devices as DevicesIcon,
  Schedule as TimeIcon,
  Campaign as UTMIcon,
  SmartToy as BotIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Launch as LaunchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { analyticsApi } from '../utils/api';
// import AnalyticsDebug from '../components/AnalyticsDebug'; // Debug component removed

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF7C7C'];

const EnhancedAnalytics: React.FC = () => {
  const { urlId } = useParams<{ urlId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [excludeBots, setExcludeBots] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Analytics data states
  const [geographicData, setGeographicData] = useState<any>(null);
  const [deviceData, setDeviceData] = useState<any>(null);
  const [timeData, setTimeData] = useState<any>(null);
  const [utmData, setUTMData] = useState<any>(null);
  const [botData, setBotData] = useState<any>(null);
  const [urlInfo, setUrlInfo] = useState<any>(null);
  
  // UTM details dialog state
  const [utmDetailsOpen, setUtmDetailsOpen] = useState(false);
  const [selectedUtmDetails, setSelectedUtmDetails] = useState<any>(null);

  const fetchAnalytics = async (endpoint: string) => {
    if (!urlId) return null;
    
    try {
      const params: any = {
        exclude_bots: excludeBots
      };
      
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await analyticsApi.getEnhancedAnalytics(
        parseInt(urlId), 
        endpoint, 
        params
      );
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch analytics');
      }
      
      return response.data;
    } catch (err: any) {
      console.error(`Error fetching ${endpoint} analytics:`, err);
      throw err;
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [overview, geographic, devices, time, utm, bots] = await Promise.all([
        fetchAnalytics('overview'),
        fetchAnalytics('geographic'),
        fetchAnalytics('devices'),
        fetchAnalytics('time'),
        fetchAnalytics('utm'),
        fetchAnalytics('bots')
      ]);
      
      setGeographicData(geographic);
      setDeviceData(devices);
      setTimeData(time);
      setUTMData(utm);
      setBotData(bots);
      
      // Set URL info from overview response
      if (overview?.url_info) {
        setUrlInfo(overview.url_info);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (urlId) {
      loadAnalytics();
    }
  }, [urlId, excludeBots, dateFrom, dateTo]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatChartData = (data: any[], labelKey: string, valueKey: string) => {
    // Add null check to prevent TypeError when data is undefined
    if (!data || !Array.isArray(data)) {
      return [];
    }
    return data.map(item => ({
      name: item[labelKey],
      value: parseInt(item[valueKey])
    }));
  };

  const renderGeographicAnalytics = () => {
    if (!geographicData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    
    // Extract geographic data from the new API response structure
    const countries = geographicData.countries || geographicData.geographic || geographicData.geographic_data || [];
    const cities = geographicData.cities || [];
    const regions = geographicData.regions || [];
    
    const countryChartData = formatChartData(countries, 'country', 'clicks');
    const cityChartData = formatChartData(cities.slice(0, 10), 'city', 'clicks');
    
    return (
      <Stack spacing={{ xs: 2, sm: 3 }}>
        {/* Charts Section */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Top Countries */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Top Countries ({countries.length})
              </Typography>
              {countryChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <BarChart data={countryChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="name" 
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <YAxis 
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="value" 
                      fill="#1976d2" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No country data available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Top Cities */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Top Cities ({cities.length})
              </Typography>
              {cityChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <PieChart>
                    <Pie
                      data={cityChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent ? percent * 100 : 0).toFixed(0)}%`}
                      outerRadius={isMobile ? 70 : 80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {cityChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No city data available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Detailed Tables */}
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            gutterBottom 
            sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
          >
            Detailed Geographic Data
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ fontWeight: 600, color: 'text.primary' }}
                >
                  Countries
                </Typography>
                <TableContainer 
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    maxHeight: 400
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600,
                            backgroundColor: 'grey.50'
                          }}
                        >
                          Country
                        </TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            fontWeight: 600,
                            backgroundColor: 'grey.50'
                          }}
                        >
                          Clicks
                        </TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            fontWeight: 600,
                            backgroundColor: 'grey.50'
                          }}
                        >
                          Percentage
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {countries.map((country: any, index: number) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:hover': { backgroundColor: 'grey.50' },
                            '&:last-child td': { border: 0 }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {country.country}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {country.clicks.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {((country.clicks / countries.reduce((sum: number, c: any) => sum + c.clicks, 0)) * 100).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ fontWeight: 600, color: 'text.primary' }}
                >
                  Cities
                </Typography>
                <TableContainer 
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    maxHeight: 400
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600,
                            backgroundColor: 'grey.50'
                          }}
                        >
                          City
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600,
                            backgroundColor: 'grey.50'
                          }}
                        >
                          Country
                        </TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            fontWeight: 600,
                            backgroundColor: 'grey.50'
                          }}
                        >
                          Clicks
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cities.map((city: any, index: number) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:hover': { backgroundColor: 'grey.50' },
                            '&:last-child td': { border: 0 }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {city.city || '-'}
                          </TableCell>
                          <TableCell>
                            {city.country || '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {city.clicks.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                      {cities.length === 0 && countries.map((country: any, index: number) => (
                        <TableRow key={`country-${index}`}>
                          <TableCell>-</TableCell>
                          <TableCell>{country.country}</TableCell>
                          <TableCell align="right">{country.clicks}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    );
  };

  const renderDeviceAnalytics = () => {
    if (!deviceData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    
    // Extract device data from the API response structure
    const devData = deviceData.device_types || deviceData.device_analytics || [];
    const osData = deviceData.operating_systems || [];
    const browserData = deviceData.browsers || [];
    
    const deviceChartData = formatChartData(devData, 'device_type', 'clicks');
    const osChartData = formatChartData(osData, 'operating_system', 'clicks');
    const browserChartData = formatChartData(browserData, 'browser_version', 'clicks');
    
    return (
      <Stack spacing={{ xs: 2, sm: 3 }}>
        {/* Charts Section */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Device Types */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Device Types ({devData.length})
              </Typography>
              {deviceChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <PieChart>
                    <Pie
                      data={deviceChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent ? percent * 100 : 0).toFixed(0)}%`}
                      outerRadius={isMobile ? 70 : 80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {deviceChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No device data available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Operating Systems */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Operating Systems ({osData.length})
              </Typography>
              {osChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <BarChart data={osChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <YAxis 
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="value" 
                      fill="#82ca9d" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No OS data available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Browser Versions */}
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            gutterBottom 
            sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
          >
            Browser Versions ({browserData.length})
          </Typography>
          {browserChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
              <BarChart data={browserChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100}
                  fontSize={12}
                  tick={{ fill: '#666' }}
                />
                <YAxis 
                  fontSize={12}
                  tick={{ fill: '#666' }}
                />
                <RechartsTooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Bar 
                  dataKey="value" 
                  fill="#ffc658" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No browser data available</Typography>
            </Box>
          )}
        </Paper>
      </Stack>
    );
  };

  const renderTimeAnalytics = () => {
    if (!timeData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    
    // Extract hourly and daily patterns from the new API structure
    const hourlyPatterns = timeData.hourly_patterns || [];
    const dailyPatterns = timeData.daily_patterns || [];
    const peakHours = timeData.peak_hours || [];
    const peakDays = timeData.peak_days || [];
    
    // Format hourly data for chart
    const hourlyChartData = hourlyPatterns.map((item: any) => ({
      hour: `${item.hour_of_day}:00`,
      clicks: parseInt(item.clicks)
    }));
    
    // Format daily data for chart
    const dailyChartData = dailyPatterns.map((item: any) => ({
      day: item.day_name || `Day ${item.day_of_week}`,
      clicks: parseInt(item.clicks)
    }));
    
    return (
      <Stack spacing={{ xs: 2, sm: 3 }}>
        {/* Peak Summary Cards */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Peak Hours Summary */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Peak Hours
              </Typography>
              {peakHours.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {peakHours.map((hour: any, index: number) => (
                    <Chip
                      key={index}
                      label={`${hour.hour_of_day}:00 (${hour.clicks.toLocaleString()} clicks)`}
                      sx={{ 
                        backgroundColor: 'primary.50',
                        color: 'primary.700',
                        fontWeight: 500
                      }}
                      size={isMobile ? "small" : "medium"}
                    />
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No peak hours data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Peak Days Summary */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Peak Days
              </Typography>
              {peakDays.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {peakDays.map((day: any, index: number) => (
                    <Chip
                      key={index}
                      label={`${day.day_name || `Day ${day.day_of_week}`} (${day.clicks.toLocaleString()} clicks)`}
                      sx={{ 
                        backgroundColor: 'success.50',
                        color: 'success.700',
                        fontWeight: 500
                      }}
                      size={isMobile ? "small" : "medium"}
                    />
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No peak days data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
        
        {/* Charts Section */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Hourly Patterns Chart */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Hourly Click Patterns
              </Typography>
              {hourlyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <LineChart data={hourlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="hour" 
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <YAxis 
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="clicks" 
                      stroke="#1976d2" 
                      strokeWidth={2}
                      dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No hourly data available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Daily Patterns Chart */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Daily Click Patterns
              </Typography>
              {dailyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <BarChart data={dailyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="day" 
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <YAxis 
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="clicks" 
                      fill="#82ca9d" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No daily data available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    );
  };

  const handleUtmClick = async (utmType: string, utmValue: string) => {
     try {
       // Fetch detailed UTM data with referrer URLs
       const response = await fetch(
         `/enhanced_analytics_api.php?url_id=${urlId}&type=utm_details&exclude_bots=${excludeBots}`,
         {
           headers: {
             'Authorization': `Bearer ${localStorage.getItem('token')}`
           }
         }
       );
       
       if (!response.ok) {
         throw new Error('Failed to fetch UTM details');
       }
       
       const data = await response.json();
       let details = null;
       
       if (utmType === 'source') {
         details = (data.data.utm_sources || []).find((item: any) => item.utm_source === utmValue);
       } else if (utmType === 'medium') {
         details = (data.data.utm_mediums || []).find((item: any) => item.utm_medium === utmValue);
       } else if (utmType === 'campaign') {
         details = (data.data.utm_campaigns || []).find((item: any) => item.utm_campaign === utmValue);
       }
       
       setSelectedUtmDetails({ type: utmType, value: utmValue, details });
       setUtmDetailsOpen(true);
     } catch (error) {
       console.error('Error fetching UTM details:', error);
       setError('Failed to fetch UTM details');
     }
   };

  const renderUTMAnalytics = () => {
    if (!utmData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    
    // Extract UTM data from the API response structure
    const utmAnalytics = utmData.utm_analytics || utmData;
    const utmSources = utmAnalytics.utm_sources || [];
    const utmMediums = utmAnalytics.utm_mediums || [];
    const utmCampaigns = utmAnalytics.utm_campaigns || [];
    
    const sourceChartData = formatChartData(utmSources, 'utm_source', 'clicks');
    const campaignChartData = formatChartData(utmCampaigns, 'utm_campaign', 'clicks');
    
    return (
      <>
        <Stack spacing={{ xs: 2, sm: 3 }}>
          {/* Charts Section */}
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* UTM Sources */}
            <Grid item xs={12} lg={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  gutterBottom 
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
                >
                  UTM Sources ({utmSources.length})
                </Typography>
                {sourceChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <PieChart>
                      <Pie
                        data={sourceChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent ? percent * 100 : 0).toFixed(0)}%`}
                        outerRadius={isMobile ? 70 : 80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {sourceChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No UTM source data available</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* UTM Campaigns */}
            <Grid item xs={12} lg={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  gutterBottom 
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
                >
                  UTM Campaigns ({utmCampaigns.length})
                </Typography>
                {campaignChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <BarChart data={campaignChartData.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45} 
                        textAnchor="end" 
                        height={100}
                        fontSize={12}
                        tick={{ fill: '#666' }}
                      />
                      <YAxis 
                        fontSize={12}
                        tick={{ fill: '#666' }}
                      />
                      <RechartsTooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Bar 
                        dataKey="value" 
                        fill="#82ca9d" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No UTM campaign data available</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
          
          {/* Detailed UTM Data */}
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              gutterBottom 
              sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
            >
              UTM Parameter Details
            </Typography>
            
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    UTM Sources
                  </Typography>
                  {utmSources.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {utmSources.map((source: any, index: number) => (
                        <Chip
                          key={index}
                          label={`${source.utm_source} (${source.clicks})`}
                          sx={{ cursor: 'pointer' }}
                          color="primary"
                          size={isMobile ? "small" : "medium"}
                          onClick={() => handleUtmClick('source', source.utm_source)}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">No UTM sources tracked</Typography>
                  )}
                </Stack>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    UTM Mediums
                  </Typography>
                  {utmMediums.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {utmMediums.map((medium: any, index: number) => (
                        <Chip
                          key={index}
                          label={`${medium.utm_medium} (${medium.clicks})`}
                          sx={{ cursor: 'pointer' }}
                          color="secondary"
                          size={isMobile ? "small" : "medium"}
                          onClick={() => handleUtmClick('medium', medium.utm_medium)}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">No UTM mediums tracked</Typography>
                  )}
                </Stack>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    UTM Campaigns
                  </Typography>
                  {utmCampaigns.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {utmCampaigns.map((campaign: any, index: number) => (
                        <Chip
                          key={index}
                          label={`${campaign.utm_campaign} (${campaign.clicks})`}
                          sx={{ cursor: 'pointer' }}
                          color="success"
                          size={isMobile ? "small" : "medium"}
                          onClick={() => handleUtmClick('campaign', campaign.utm_campaign)}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">No UTM campaigns tracked</Typography>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Stack>
        
        {/* UTM Details Dialog */}
        <Dialog
          open={utmDetailsOpen}
          onClose={() => setUtmDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                UTM {selectedUtmDetails?.type} Details: {selectedUtmDetails?.value}
              </Typography>
              <IconButton onClick={() => setUtmDetailsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedUtmDetails?.details && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  <strong>Clicks:</strong> {selectedUtmDetails.details.clicks}
                </Typography>
                
                {selectedUtmDetails.details.referrer_urls && selectedUtmDetails.details.referrer_urls.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="h6" gutterBottom>
                      Referrer URLs
                    </Typography>
                    <List>
                      {selectedUtmDetails.details.referrer_urls.map((referrer: any, index: number) => (
                        <ListItem key={index} divider>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Link
                                  href={referrer.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ wordBreak: 'break-all' }}
                                >
                                  {referrer.url}
                                </Link>
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(referrer.url, '_blank')}
                                >
                                  <LaunchIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            }
                            secondary={`Clicks: ${referrer.clicks}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                
                {(!selectedUtmDetails.details.referrer_urls || selectedUtmDetails.details.referrer_urls.length === 0) && (
                  <Typography color="textSecondary" mt={2}>
                    No referrer URLs available for this UTM parameter.
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUtmDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  const renderBotAnalytics = () => {
    if (!botData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    
    // Extract bot data from the API response structure
    const botStats = botData.bot_stats || botData.bot_data || botData;
    const statistics = botStats.statistics || botStats;
    const botStatsData = [
      { name: 'Human Clicks', value: parseInt(statistics.human_clicks) || 0 },
      { name: 'Bot Clicks', value: parseInt(statistics.bot_clicks) || 0 }
    ];
    
    const botTypes = botStats.bot_types || [];
    const botTypesChartData = formatChartData(botTypes, 'bot_type', 'clicks');
    
    return (
      <Stack spacing={{ xs: 2, sm: 3 }}>
        {/* Charts Section */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Bot vs Human Traffic */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Bot vs Human Traffic
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <PieChart>
                  <Pie
                    data={botStatsData}
                    cx="50%"
                    cy="50%"
                    outerRadius={isMobile ? 70 : 80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent ? percent * 100 : 0).toFixed(1)}%`}
                  >
                    <Cell fill="#00C49F" />
                    <Cell fill="#FF8042" />
                  </Pie>
                  <RechartsTooltip 
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          
          {/* Bot Types */}
          <Grid item xs={12} lg={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
              >
                Bot Types Detected ({botTypes.length})
              </Typography>
              {botTypesChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <BarChart data={botTypesChartData.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <YAxis 
                      fontSize={12}
                      tick={{ fill: '#666' }}
                    />
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="value" 
                      fill="#ff7300" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No bot types detected</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
        
        {/* Bot Statistics Summary */}
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            gutterBottom 
            sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}
          >
            Bot Detection Summary
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  sx={{ fontWeight: 700, color: 'primary.main' }}
                >
                  {(statistics.total_clicks || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Clicks
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  sx={{ fontWeight: 700, color: 'success.main' }}
                >
                  {(statistics.human_clicks || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Human Clicks
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  sx={{ fontWeight: 700, color: 'warning.main' }}
                >
                  {(statistics.bot_clicks || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bot Clicks
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  sx={{ fontWeight: 700, color: 'error.main' }}
                >
                  {(statistics.bot_percentage || 0).toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bot Percentage
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          {botTypes.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography 
                variant="subtitle1" 
                sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}
              >
                Detected Bot Types
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {botTypes.map((bot: any, index: number) => (
                  <Chip
                    key={index}
                    label={`${bot.bot_type} (${bot.clicks})`}
                    color="warning"
                    size={isMobile ? "small" : "medium"}
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      </Stack>
    );
  };

  if (loading && !geographicData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 }
      }}
    >
      {/* Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              mb: 0.5
            }}
          >
            Enhanced Analytics
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            Advanced insights with geographic, device, and behavioral data
          </Typography>
        </Box>
        {urlInfo && (
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              mt: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack spacing={{ xs: 2, sm: 3 }}>
              <Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  gutterBottom
                  sx={{ fontWeight: 500 }}
                >
                  Short URL
                </Typography>
                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  component="a"
                  href={urlInfo.short_url || `${window.location.origin}/${urlInfo.short_code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' },
                    wordBreak: 'break-all'
                  }}
                >
                  {urlInfo.short_url || `${window.location.origin}/${urlInfo.short_code}`}
                </Typography>
              </Box>
              
              <Divider />
              
              <Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  gutterBottom
                  sx={{ fontWeight: 500 }}
                >
                  Original URL
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    wordBreak: 'break-all',
                    color: 'text.primary',
                    lineHeight: 1.5
                  }}
                  title={urlInfo.long_url}
                >
                  {urlInfo.long_url}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}
      </Box>

      {/* Controls */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          gutterBottom 
          sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}
        >
          Filters & Controls
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={excludeBots}
                onChange={(e) => setExcludeBots(e.target.checked)}
                color="primary"
              />
            }
            label="Exclude Bot Traffic"
            sx={{ 
              '& .MuiFormControlLabel-label': {
                fontWeight: 500
              }
            }}
          />
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 2, sm: 2 }}
            alignItems={{ xs: 'stretch', sm: 'flex-end' }}
          >
            <TextField
              label="Start Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                flex: { xs: 1, sm: 1 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
            
            <TextField
              label="End Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                flex: { xs: 1, sm: 1 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
            
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadAnalytics}
              disabled={loading}
              size={isMobile ? "small" : "medium"}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Analytics Tabs */}
      <Paper 
        elevation={0}
        sx={{ 
          mb: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="analytics tabs"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: { xs: 48, sm: 56 },
              gap: 1
            },
            '& .Mui-selected': {
              fontWeight: 600
            }
          }}
        >
          <Tab icon={<GeographicIcon />} label="Geographic" />
          <Tab icon={<DevicesIcon />} label="Devices" />
          <Tab icon={<TimeIcon />} label="Time Patterns" />
          <Tab icon={<UTMIcon />} label="UTM Tracking" />
          <Tab icon={<BotIcon />} label="Bot Detection" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            {renderGeographicAnalytics()}
          </Box>
        </TabPanel>
      <TabPanel value={tabValue} index={1}>
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            {renderDeviceAnalytics()}
          </Box>
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            {renderTimeAnalytics()}
          </Box>
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            {renderUTMAnalytics()}
          </Box>
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            {renderBotAnalytics()}
          </Box>
        </TabPanel>
    </Box>
  );
};

export default EnhancedAnalytics;